import { Permission } from '@bookora/shared';

import { helpHref } from '../deep-link-parse';
import type { Persona } from '../persona';
import { resolveSteps, tourAllowed } from '../resolve-steps';
import { ACCOUNTING_TOUR } from './accounting';
import { AI_IMPORT_CAPTURE_TOUR } from './ai-import-capture';
import { AI_IMPORT_REVIEW_TOUR } from './ai-import-review';
import { APPOINTMENT_FORM_TOUR } from './appointment-form';
import { CALENDAR_TOUR } from './calendar';
import { CORE_TOUR } from './core';
import { CRM_TOUR } from './crm';
import { DASHBOARD_TOUR } from './dashboard';
import { INVENTORY_TOUR } from './inventory';
import { PACKAGES_TOUR } from './packages';
import { PATIENT_FILE_TOUR } from './patient-file';
import { PATIENTS_TOUR } from './patients';
import { PLANNED_TOURS, isPlannedTour } from './planned';
import { PROCEDURES_TOUR } from './procedures';
import { SESSION_FORM_TOUR } from './session-form';
import { STAFF_TOUR } from './staff';
import { BRANCHES_TOUR_FILLED } from './branches';
import { RAAS_RESULTS_TOUR } from './raas-results';
import {
  NO_TOUR_FEATURES,
  type StepContext,
  type TourDef,
  type TourFeature,
  type TourFinish,
  type TourKey,
} from './types';
import { WORKING_HOURS_TOUR } from './working-hours';

/**
 * Every tour, by key. Tours are code: a new one ships with a panel deploy.
 * Planned keys resolve to their step-less stubs (`planned.ts`).
 */
export const TOURS: Readonly<Record<TourKey, TourDef>> = {
  ...PLANNED_TOURS,
  core: { ...CORE_TOUR, finish: coreFinish() },
  dashboard: DASHBOARD_TOUR,
  calendar: CALENDAR_TOUR,
  'appointment-form': APPOINTMENT_FORM_TOUR,
  patients: PATIENTS_TOUR,
  'patient-file': PATIENT_FILE_TOUR,
  'session-form': SESSION_FORM_TOUR,
  packages: PACKAGES_TOUR,
  inventory: INVENTORY_TOUR,
  crm: CRM_TOUR,
  accounting: ACCOUNTING_TOUR,
  staff: STAFF_TOUR,
  procedures: PROCEDURES_TOUR,
  'working-hours': WORKING_HOURS_TOUR,
  'ai-import-capture': AI_IMPORT_CAPTURE_TOUR,
  'ai-import-review': AI_IMPORT_REVIEW_TOUR,
  // Pricing build: «نتایج و صورت‌حساب» (panel-billing) and «شعبه‌ها» (panel-branches-ops).
  'raas-results': RAAS_RESULTS_TOUR,
  branches: BRANCHES_TOUR_FILLED,
};

/**
 * The hub's «راهنمای بخش‌ها» order (core lives in «شروع کار»): the day's
 * desk work first, then the clinic's records, then running the clinic, then
 * the photo import. `tourForPath` walks it too; no two page routes overlap.
 */
export const TOUR_ORDER: readonly TourKey[] = [
  'dashboard',
  'calendar',
  'appointment-form',
  'patients',
  'patient-file',
  'session-form',
  'packages',
  'inventory',
  'crm',
  'accounting',
  'staff',
  'procedures',
  'working-hours',
  'raas-results',
  'branches',
  'ai-import-capture',
  'ai-import-review',
];

/** A key with a tour that has steps (a planned stub is not linkable yet). */
export function isTourKey(value: string): value is TourKey {
  return (
    Object.prototype.hasOwnProperty.call(TOURS, value) && !isPlannedTour(TOURS[value as TourKey])
  );
}

/**
 * The page tour of this location, if any («راهنمای این صفحه»). `search` is the
 * query string (with its `?`, or `''`).
 */
export function tourForPath(pathname: string, search = ''): TourDef | null {
  for (const key of TOUR_ORDER) {
    const tour = TOURS[key];
    if (tour.surface === 'page' && tour.route?.match(pathname, search)) return tour;
  }
  return null;
}

/* ── recommendations ─────────────────────────────────────────────────────── */

/**
 * The tours worth taking next, per persona, most useful first. Personas pick
 * the ORDER; whether a tour is listed at all is still its own gate and step
 * filters (a custom role gets what its keys open).
 */
const RECOMMENDED: Readonly<Record<Persona, readonly TourKey[]>> = {
  owner: ['calendar', 'patients', 'crm', 'inventory', 'accounting', 'staff'],
  doctor: ['calendar', 'patient-file', 'session-form'],
  clinical: ['calendar', 'patient-file', 'session-form', 'inventory'],
  'front-desk': ['calendar', 'appointment-form', 'patients', 'patient-file', 'dashboard'],
  finance: ['accounting', 'dashboard'],
  staff: ['dashboard', 'patients'],
};

/**
 * The recommended tours this viewer may actually take: shipped, allowed
 * (permissions, and features when given — without them a feature-gated tour
 * is left out), and with at least `minSteps` steps for this viewer. For the
 * hub's «شروع کار» and the core tour's «بعدی: …». The caller filters out
 * tours already seen if it has the guidance state.
 */
export function recommendedTours(
  ctx: StepContext,
  features: Readonly<Record<TourFeature, boolean>> = NO_TOUR_FEATURES,
): TourKey[] {
  return RECOMMENDED[ctx.persona].filter((key) => {
    const tour = TOURS[key];
    if (isPlannedTour(tour)) return false;
    if (!tourAllowed(tour, ctx.permissions, features)) return false;
    return resolveSteps(tour, ctx).length >= tour.minSteps;
  });
}

/** The first recommended tour a link can start directly (a page tour, not a context one). */
function nextPageTour(ctx: StepContext): TourDef | null {
  for (const key of recommendedTours(ctx)) {
    const tour = TOURS[key];
    if (tour.surface === 'page' && tour.route && !tour.route.context) return tour;
  }
  return null;
}

function setupOpen(ctx: StepContext): boolean {
  return (
    ctx.persona === 'owner' &&
    ctx.onboardingIncomplete === true &&
    ctx.can(Permission.SETTINGS_EDIT)
  );
}

/**
 * The core tour's finish (critic C7), resolved per viewer: an owner with
 * setup still open goes back to it — the next open step's own page when the
 * context knows it, else the checklist — which is what the new-owner welcome
 * promises. Everyone else is offered the first recommended page tour, through
 * a `?help=tour:` link (source `deeplink`, no proactive budget). With
 * neither, the tour ends on the plain «متوجه شدم».
 */
function coreFinish(): TourFinish {
  return {
    label: (ctx) => {
      if (setupOpen(ctx)) return 'ادامهٔ راه‌اندازی';
      const next = nextPageTour(ctx);
      return next ? `بعدی: ${next.title}` : '';
    },
    to: (ctx) => {
      if (setupOpen(ctx)) return ctx.nextSetupPath ?? '/setup';
      const next = nextPageTour(ctx);
      return next?.route ? helpHref(next.route.path, { tour: next.key }) : '/';
    },
    when: (ctx) => setupOpen(ctx) || nextPageTour(ctx) !== null,
  };
}
